import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../store';
import {
  connectDepartmentToResource,
  disconnectDepartmentFromResource,
  fetchDepartmentsListLightweight,
} from '../../store/actions/collaboratorActions';
import { showModal, hideModal } from '../../store/actions/modalActions';
import Dropdown from './TailwindComponents/Dropdown';
import Button from './TailwindComponents/Button';
import Badge from './TailwindComponents/Badge';
import { PiBuildingLight, PiTrashLight } from 'react-icons/pi';
import Spinner from './TailwindComponents/Spinner';

/**
 * Departments as collaborators (UI feedback G-6).
 *
 * The twin of `TeamConnectorSection`: pick a department, connect it, and every
 * member gets a collaborator row with `source='department'` at the default
 * VIEW access. Disconnecting removes exactly those rows (direct shares are
 * untouched). Deliberately lean - a department grants READ by default and the
 * per-member access is tuned on the collaborator rows themselves, so there is
 * no per-member override step here.
 */

interface ConnectedDepartment {
  department_uuid: string;
  department_name: string;
  member_count: number;
}

interface Props {
  resourceUuid: string;
  resourceType: string;
  connectedDepartments: ConnectedDepartment[];
  onDepartmentConnected: () => void;
  canAdmin?: boolean;
}

const DepartmentConnectorSection: React.FC<Props> = ({
  resourceUuid,
  resourceType,
  connectedDepartments,
  onDepartmentConnected,
  canAdmin = true,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [departments, setDepartments] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [connecting, setConnecting] = useState(false);

  useEffect(() => {
    setLoading(true);
    (dispatch as any)(fetchDepartmentsListLightweight())
      .then((result: any) => setDepartments(result || []))
      .finally(() => setLoading(false));
  }, [dispatch]);

  const connectedIds = useMemo(
    () => new Set(connectedDepartments.map((cd) => cd.department_uuid)),
    [connectedDepartments]
  );

  const options = useMemo(
    () =>
      departments
        .filter((d) => !connectedIds.has(d.id || d.uuid))
        .map((d) => ({ value: d.id || d.uuid, label: `${d.name}${d.member_count ? ` (${d.member_count})` : ''}` })),
    [departments, connectedIds]
  );

  const handleConnect = async () => {
    if (!selectedId) return;
    setConnecting(true);
    try {
      const result = await (dispatch as any)(
        connectDepartmentToResource(resourceType, resourceUuid, selectedId)
      );
      if (result) {
        setSelectedId(null);
        onDepartmentConnected();
      }
    } finally {
      setConnecting(false);
    }
  };

  if (!canAdmin) return null;

  return (
    <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
      <div className="flex items-center gap-2 mb-3">
        <PiBuildingLight className="w-4 h-4 text-primary-600" />
        <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
          {t('Departments')}
        </p>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 py-3 justify-center">
          <Spinner size="glyph" className="!h-5 !w-5" />
          <span className="text-xs text-gray-400">{t('Loading')}...</span>
        </div>
      ) : (
        <div className="flex items-start gap-2">
          <div className="flex-1 min-w-0">
            <Dropdown
              options={options}
              value={selectedId || ''}
              onChange={(val) => setSelectedId((Array.isArray(val) ? val[0] : val) || null)}
              placeholder={options.length === 0 ? t('No departments available') : t('Select a department to connect')}
              width="w-full"
            />
          </div>
          <Button variant="primary" size="small" onClick={handleConnect} disabled={!selectedId || connecting}>
            {connecting ? t('Adding...') : t('Add')}
          </Button>
        </div>
      )}
      <p className="mt-2 text-[11px] text-gray-400">
        {t('Members get view access by default; adjust each person afterwards.')}
      </p>
    </div>
  );
};

/** Connected departments, with a disconnect control (mirrors ConnectedTeamsList). */
export const ConnectedDepartmentsList: React.FC<{
  resourceUuid: string;
  resourceType: string;
  connectedDepartments: ConnectedDepartment[];
  onDepartmentDisconnected: () => void;
  canAdmin?: boolean;
}> = ({ resourceUuid, resourceType, connectedDepartments, onDepartmentDisconnected, canAdmin = true }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [disconnecting, setDisconnecting] = useState<Set<string>>(new Set());

  const handleDisconnect = (departmentUuid: string, name: string) => {
    dispatch(showModal({
      showModal: true,
      type: 'confirmation',
      message: t('Disconnect department'),
      subMsg: t('Disconnect "{{name}}"? All department members will lose access granted through it.', { name }),
      isDest: true,
      onConfirm: async () => {
        dispatch(hideModal());
        setDisconnecting((prev) => new Set(prev).add(departmentUuid));
        try {
          const result = await (dispatch as any)(
            disconnectDepartmentFromResource(resourceType, resourceUuid, departmentUuid)
          );
          if (result) onDepartmentDisconnected();
        } finally {
          setDisconnecting((prev) => { const n = new Set(prev); n.delete(departmentUuid); return n; });
        }
      },
    }));
  };

  if (connectedDepartments.length === 0) return null;

  return (
    <div className="px-5 py-3">
      <p className="text-[11px] font-medium text-gray-400 dark:text-gray-500 mb-2">
        {t('Connected departments')}
      </p>
      <div className="space-y-1">
        {connectedDepartments.map((cd) => (
          <div
            key={cd.department_uuid}
            className="flex items-center gap-2 py-2 px-3 rounded-lg border border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/30"
          >
            <PiBuildingLight className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
            <span className="text-sm text-gray-900 dark:text-white truncate flex-1 min-w-0">{cd.department_name}</span>
            <Badge color="gray">{cd.member_count}</Badge>
            {canAdmin && (
              <button
                onClick={() => handleDisconnect(cd.department_uuid, cd.department_name)}
                disabled={disconnecting.has(cd.department_uuid)}
                className="p-1 rounded text-gray-400 hover:text-danger-500 hover:bg-danger-50 dark:hover:bg-danger-900/20 transition-colors disabled:opacity-50 flex-shrink-0"
                title={t('Disconnect department')}
              >
                <PiTrashLight className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default DepartmentConnectorSection;
